import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgbActiveModal,
  NgbDatepickerModule,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCalendarDays,
  faClock,
} from '@fortawesome/free-solid-svg-icons';

import { ScheduleRow } from '../schedule-table/schedule-table.component';

export type ScheduleRescheduleModalResult = {
  row: ScheduleRow;
  sendAt: string;
};

type RescheduleForm = {
  date: FormControl<NgbDateStruct | null>;
  time: FormControl<string>;
};

@Component({
  selector: 'app-schedule-reschedule-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDatepickerModule,
    FontAwesomeModule,
  ],
  templateUrl: './schedule-reschedule-modal.component.html',
  styleUrls: ['./schedule-reschedule-modal.component.css'],
})
export class ScheduleRescheduleModalComponent implements OnInit {
  @Input({ required: true }) row!: ScheduleRow;

  readonly faCalendarDays = faCalendarDays;
  readonly faClock = faClock;

  readonly today: NgbDateStruct = this.getTodayDateStruct();

  readonly form = new FormGroup<RescheduleForm>({
    date: new FormControl<NgbDateStruct | null>(null, {
      validators: [Validators.required],
    }),
    time: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  constructor(public readonly modal: NgbActiveModal) {}

  ngOnInit(): void {
    const scheduledDate = this.toValidDate(this.row?.scheduledFor);

    this.form.patchValue({
      date: this.toUtcDateStruct(scheduledDate),
      time: this.toUtcTimeInputValue(scheduledDate),
    });
  }

  get date(): FormControl<NgbDateStruct | null> {
    return this.form.controls.date;
  }

  get time(): FormControl<string> {
    return this.form.controls.time;
  }

  updateSchedule(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const selectedDate = this.date.value;
    const selectedTime = this.time.value;

    if (!selectedDate || !selectedTime) {
      return;
    }

    const sendAt = this.combineDateAndTimeToUtcIso(
      selectedDate,
      selectedTime,
    );

    this.modal.close({
      row: this.row,
      sendAt,
    } satisfies ScheduleRescheduleModalResult);
  }

  private combineDateAndTimeToUtcIso(
    date: NgbDateStruct,
    time: string,
  ): string {
    const [hours, minutes] = this.parseTime(time);

    const utcTimestamp = Date.UTC(
      date.year,
      date.month - 1,
      date.day,
      hours,
      minutes,
      0,
      0,
    );

    return new Date(utcTimestamp).toISOString();
  }

  private parseTime(time: string): [number, number] {
    const [hoursValue = '0', minutesValue = '0'] = time.split(':');

    const hours = Number(hoursValue);
    const minutes = Number(minutesValue);

    return [
      Number.isFinite(hours) ? hours : 0,
      Number.isFinite(minutes) ? minutes : 0,
    ];
  }

  private toValidDate(value?: string | Date | null): Date {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? new Date() : value;
    }

    const date = new Date(value ?? '');

    return Number.isNaN(date.getTime()) ? new Date() : date;
  }

  private toUtcDateStruct(value?: string | Date | null): NgbDateStruct {
    const date = this.toValidDate(value);

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  /**
   * Uses UTC hours so 11:10Z remains 11:10 in the time input.
   */
  private toUtcTimeInputValue(value?: string | Date | null): string {
    const date = this.toValidDate(value);

    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  }

  /**
   * The minimum date follows the user's current local calendar date.
   */
  private getTodayDateStruct(): NgbDateStruct {
    const today = new Date();

    return {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
  }
}